import FeatherIcon from 'feather-icons-react';
import CountUp from 'react-countup';
import Skeleton from 'react-loading-skeleton';
import { Card, CardBody, Col, Row } from 'reactstrap';

interface Metric {
  value: number;
  label?: string;
}

type IPersonOverviewWidgetProps = {
  title: string;
  icon: string;
  metrics: Metric[];
  withBackgroundSvg?: boolean;
  hasAppInfo?: string;
  isLoading?: boolean;
};

const PersonWidgetSkeleton = ({ columns = 1 }: { columns?: number }) => (
  <Row className={columns > 1 ? 'text-center mt-n3' : 'mt-2'}>
    {Array.from({ length: columns }).map((_, i) => (
      <Col
        key={i}
        md={12 / columns}
        className={i === 0 && columns > 1 ? 'border-end' : ''}
      >
        <Skeleton
          baseColor="#e8e8e8c5"
          highlightColor="#bababa96"
          height={40}
          width={columns > 1 ? 80 : 100}
        />
        {columns > 1 && (
          <Skeleton
            baseColor="#e8e8e8c5"
            highlightColor="#bababa96"
            height={15}
            width={60}
            className="mt-2"
          />
        )}
      </Col>
    ))}
  </Row>
);

const PersonWidgetDisplay = ({
  metrics,
  isSingle,
}: {
  metrics: Metric[];
  isSingle: boolean;
}) => {
  return (
    <Row className={isSingle ? '' : 'text-center mt-n2'}>
      {metrics.map(({ value, label }, idx) => (
        <Col
          key={idx}
          md={isSingle ? 12 : 6}
          className={
            idx === 0 && !isSingle ? 'border-end border-end-dashed mb-1' : ''
          }
        >
          <h4 className="counter-value">
            <CountUp start={0} end={value} duration={1} />
          </h4>
          {!isSingle && <span className="text-muted fs-12">{label}</span>}
        </Col>
      ))}
    </Row>
  );
};

export const PersonOverviewWidget = ({
  title,
  icon,
  metrics,
  hasAppInfo,
  isLoading,
  withBackgroundSvg = false,
}: IPersonOverviewWidgetProps) => {
  const isSingle = metrics.length === 1 || Boolean(hasAppInfo);

  const getContent = () => {
    if (isLoading)
      return (
        <PersonWidgetSkeleton
          columns={metrics.length === 1 || hasAppInfo ? 1 : 2}
        />
      );
    if (hasAppInfo) return <h4>{hasAppInfo}</h4>;
    return <PersonWidgetDisplay metrics={metrics} isSingle={isSingle} />;
  };

  return (
    <Col md={4}>
      <Card className="card-animate overflow-hidden">
        {withBackgroundSvg && (
          <div
            className="position-absolute start-0 h-100"
            style={{ zIndex: 0 }}
          >
            <svg
              version="1.2"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 200 120"
              width="200"
              height="100%"
            >
              <path
                style={{ opacity: 0.05, fill: 'rgb(128 128 128)' }}
                d="m189.5-25.8c0 0 20.1 46.2-26.7 71.4 0 0-60 15.4-62.3 65.3-2.2 49.8-50.6 59.3-57.8 61.5-7.2 2.3-60.8 0-60.8 0l-11.9-199.4z"
              />
            </svg>
          </div>
        )}

        <CardBody>
          <div
            className={`d-flex justify-content-between ${isSingle ? 'mb-2' : ''}`}
          >
            <span className="fw-medium text-muted fs-16">{title}</span>

            <div className="avatar-sm flex-shrink-0">
              <span className="avatar-title bg-info-subtle rounded-circle fs-2">
                <FeatherIcon icon={icon} className="text-info" />
              </span>
            </div>
          </div>

          {getContent()}
        </CardBody>
      </Card>
    </Col>
  );
};
